// src/pages/company/CompanyTeamPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import CompanyLayout from "../../components/company/CompanyLayout";
import ApprovalCover from "../../components/company/ApprovalCover";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import { toast } from "react-hot-toast";

// Mirrors OrganizationMember.role choices in accounts/models.py.
// Keep in sync with migration 0043 and any future role additions.
const ROLES = [
  "Owner",
  "Admin",
  "Department Leader",
  "Manager",
  "Member",
  "Authorized User",
  "Time Entry - Clock",
  "Time Entry - Manual",
];

// CSV helper. With a header row, columns are matched by name in any order:
// first_name, last_name, email, role, department, type, hide_from_public.
// Without one: the older five-column order, then the names, so a file made
// before names existed still reads the same.
const CSV_COLUMNS = ["email", "role", "department", "type", "hide_from_public", "first_name", "last_name"];
const truthy = (v) => ["1", "true", "yes", "y", "x", "on"].includes(String(v || "").trim().toLowerCase());

// Only the old .xls format lands here: .xlsx is read on the server.
const EXCEL_HELP =
  "That's an older Excel (.xls) file. In Excel choose File, Save As, and pick " +
  "\"Excel Workbook (.xlsx)\" or \"CSV UTF-8 (Comma delimited)\", then upload that.";

// A plausible address: something@domain.tld with no spaces or separators. The
// server checks properly; this only keeps a misread file from looking like
// rows of people.
const EMAIL_RE = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;

async function parseCsv(file) {
  // An .xlsx is a zip archive. Read as text it became rows of symbols, and with
  // no header the first "column" of each looked like an email: "Parsed 28 rows"
  // of garbage (CamTalk, 2026-10-09). Recognised by its first bytes, it goes to
  // the server, which reads the first sheet into the same rows a CSV gives.
  const head = new Uint8Array(await file.slice(0, 4).arrayBuffer());
  const isZip = head[0] === 0x50 && head[1] === 0x4b && head[2] === 0x03 && head[3] === 0x04;
  const isOle = head[0] === 0xd0 && head[1] === 0xcf && head[2] === 0x11 && head[3] === 0xe0;
  if (isZip) {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetchWithAuth("/api/accounts/team-import/parse/", { method: "POST", body: fd });
    return (res?.rows || []).map((r) => r.map((c) => String(c ?? "").trim())).filter((r) => r.some(Boolean));
  }
  if (isOle) throw new Error(EXCEL_HELP);
  // Excel's "CSV UTF-8" starts the file with a byte-order mark, which turned the
  // first header into "\uFEFFemail" and dropped every row as having no email.
  // It also quotes fields; the quotes are not part of the value.
  const text = (await file.text()).replace(/^\uFEFF/, "");
  if (text.includes("\u0000") || (text.match(/\uFFFD/g) || []).length > 5) {
    throw new Error("That file can't be read as a CSV. Save it as \"CSV UTF-8 (Comma delimited)\" and upload it again.");
  }
  return text
    .trim()
    .split(/\r?\n/)
    .map((l) => l.split(",").map((s) => s.trim().replace(/^"(.*)"$/, "$1").trim()))
    .filter((arr) => arr.length && arr.some(Boolean));
}

// The file a company fills in. Email is the only required column. The name is
// what the person sees on arrival, to confirm or correct.
const CSV_TEMPLATE =
  "first_name,last_name,email,role,department,type,hide_from_public\r\n" +
  "Jordan,Ellis,jordan.ellis@example.com,Member,,1099,x\r\n" +
  "Priya,Shah,priya.shah@example.com,Member,,1099,x\r\n";

function downloadCsvTemplate() {
  const url = URL.createObjectURL(new Blob([CSV_TEMPLATE], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = "sift-team-invite-template.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function CompanyTeamPage() {
  const [org, setOrg] = useState(null);
  const [meId, setMeId] = useState(null);
  const [members, setMembers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [deptByName, setDeptByName] = useState({});
  const [loading, setLoading] = useState(true);

  // single invite
  // `hide` starts as null and is set once we know whether this is a staffing
  // agency, whose people are hidden by default.
  const [invite, setInvite] = useState({
    first_name: "", last_name: "",
    email: "", role: "Member", department: "", employment_type: "1099", hide: null,
  });

  // The company's own setting: are new people hidden from other companies?
  const [hideDefault, setHideDefault] = useState(false);
  const [savingHideDefault, setSavingHideDefault] = useState(false);

  // inline edits
  const [edits, setEdits] = useState({}); // { [memberId]: { role, department } }

  // parsed CSV rows as {email, role, departmentId}
  const [csvRows, setCsvRows] = useState([]);
  // Why the last file was refused, kept by the upload button until the next one.
  const [csvError, setCsvError] = useState("");
  const [csvSkipped, setCsvSkipped] = useState(0);
  // Rows the server refused, with the reason, so they can be fixed and re-sent.
  const [csvFailures, setCsvFailures] = useState([]);
  // A batch in flight. Send stayed live while one ran, and each press started
  // another batch re-inviting the same people (CamTalk pressed it 17 times on
  // 2026-10-11, which stalled the API).
  const [csvSending, setCsvSending] = useState(false);

  // Which row has a roster hold request in flight. Guards against a second
  // click landing while the first is still going, which on a toggle would
  // undo the thing that had not finished happening yet.
  const [holdBusy, setHoldBusy] = useState(null);
  // Same guard for the signing switch.
  const [signBusy, setSignBusy] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        // Fire all 3 requests in parallel
        const [meRes, deptRes, membersRes] = await Promise.all([
          fetchWithAuth("/api/accounts/me/"),
          fetchWithAuth("/api/accounts/departments/"),
          fetchWithAuth("/api/accounts/org-members/"),
        ]);

        const meJson = meRes.json ? await meRes.json() : meRes;
        setOrg(meJson?.organization || null);
        setMeId(meJson?.id ?? null);
        const hideNew = !!meJson?.organization?.hide_members_by_default;
        setHideDefault(hideNew);
        setInvite((f) => (f.hide === null ? { ...f, hide: hideNew } : f));

        const dJson = deptRes.json ? await deptRes.json() : deptRes;
        setDepartments(Array.isArray(dJson) ? dJson : []);
        const map = {};
        (Array.isArray(dJson) ? dJson : []).forEach((x) => (map[(x.name || "").toLowerCase()] = x.id));
        setDeptByName(map);

        const mJson = membersRes.json ? await membersRes.json() : membersRes;
        setMembers(Array.isArray(mJson?.results) ? mJson.results : Array.isArray(mJson) ? mJson : []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const loadMembers = async () => {
    const r = await fetchWithAuth("/api/accounts/org-members/");
    const j = r.json ? await r.json() : r;
    // endpoint returns a plain list like:
    // {id, name, email, role, department, department_id?}
    setMembers(Array.isArray(j?.results) ? j.results : Array.isArray(j) ? j : []);
  };

  // ---------- company setting: hide new people ----------
  const saveHideDefault = async (value) => {
    setSavingHideDefault(true);
    try {
      const res = await fetchWithAuth("/api/accounts/org/policy/", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ hide_members_by_default: value, organization_id: org?.id }),
      });
      if (!res?.success) throw new Error(res?.error || "Could not save the setting.");
      setHideDefault(!!res.hide_members_by_default);
      setInvite((f) => ({ ...f, hide: !!res.hide_members_by_default }));
      toast.success(value ? "New team members will be hidden." : "New team members will be visible.");
    } catch (e) {
      toast.error(e?.message || "Could not save the setting.");
    } finally {
      setSavingHideDefault(false);
    }
  };

  // ---------- single invite ----------
  const addMember = async () => {
    if (!invite.email.trim()) return toast.error("Email is required.");
    try {
      const body = {
        first_name: invite.first_name.trim(),
        last_name: invite.last_name.trim(),
        email: invite.email.trim().toLowerCase(),
        role: invite.role,
        department: invite.department || null, // expects id or null
        employment_type: invite.employment_type,
        hide_from_public: !!invite.hide,
      };
      const res = await fetchWithAuth("/api/accounts/add-org-member/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res?.ok) throw new Error(res?.error || "Could not add member.");
      toast.success(
        res.pending
          ? `${body.email} already uses Sift. We sent them a link to join; they're added when they accept.`
          : res.already_member
          ? `${body.email} is already on the team. We sent their invite again.`
          : res.emailed
            ? `Invite sent to ${body.email}.`
            : `${body.email} was added, but the email did not send. Try again in a minute.`
      );
      setInvite((x) => ({ ...x, email: "", first_name: "", last_name: "" }));
      await loadMembers();
    } catch (e) {
      toast.error(e?.message || "Could not add member.");
    }
  };

  // ---------- CSV upload ----------
  const handleCsvFile = async (file) => {
    let rows;
    try {
      rows = await parseCsv(file);
    } catch (e) {
      setCsvRows([]);
      setCsvError(e?.message || "That file couldn't be read.");
      return;
    }
    setCsvError("");
    // A header row is matched by column name; otherwise the default order.
    const headerish = rows[0]?.some((c) => (c || "").toLowerCase().includes("email"));
    const cols = headerish
      ? rows[0].map((c) => (c || "").trim().toLowerCase().replace(/\s+/g, "_"))
      : CSV_COLUMNS;
    const at = (row, name) => {
      const i = cols.indexOf(name);
      return i >= 0 ? (row[i] || "").trim() : "";
    };
    // A blank cell follows the company's setting, not the single-invite box.
    const agencyDefault = !!hideDefault;
    const data = (headerish ? rows.slice(1) : rows).map((row) => {
      const role = at(row, "role");
      const department = at(row, "department");
      const hideRaw = at(row, "hide_from_public") || at(row, "hide");
      let departmentId = "";
      if (department) {
        const byName = deptByName[department.toLowerCase()];
        departmentId = byName || (Number.isFinite(+department) ? +department : department);
      }
      return {
        first_name: at(row, "first_name"),
        last_name: at(row, "last_name"),
        email: at(row, "email").toLowerCase(),
        // Sent as typed: the server checks it and reports a bad role by row,
        // rather than this silently turning a typo into "Member".
        role: role || "Member",
        department: departmentId || null,
        employment_type: at(row, "type") || at(row, "employment_type") || "1099",
        hide_from_public: hideRaw === "" ? agencyDefault : truthy(hideRaw),
      };
    });
    const valid = data.filter((r) => EMAIL_RE.test(r.email));
    setCsvRows(valid);
    setCsvSkipped(data.length - valid.length);
    setCsvFailures([]);
    if (!valid.length) {
      setCsvError("No email addresses found in that file. Check it has an email column, or start from the template.");
      return;
    }
    toast.success(`Found ${valid.length} ${valid.length === 1 ? "person" : "people"} to invite.`);
  };

  const sendCsvInvites = async () => {
    if (!csvRows.length || csvSending) return;
    setCsvSending(true);
    try {
      const res = await fetchWithAuth("/api/accounts/add-org-members-batch/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rows: csvRows }),
      });
      if (!res?.ok) throw new Error(res?.error || "Batch invite failed.");
      const failed = res.failed || [];
      setCsvFailures(failed);
      // The emails are sent by the worker after this returns, a few a second.
      const queued = res.queued || 0;
      const unsent = csvRows.length - failed.length - queued;
      toast.success(
        `Inviting ${queued} of ${csvRows.length}. The emails go out over the next few minutes.` +
          (failed.length ? ` ${failed.length} row(s) need fixing.` : "") +
          (unsent > 0 ? ` ${unsent} couldn't be emailed; invite them again from the form above.` : "")
      );
      setCsvRows([]);
      await loadMembers();
    } catch (e) {
      toast.error(e?.message || "Batch invite failed.");
    } finally {
      setCsvSending(false);
    }
  };

  // ---------- edits ----------
  // Returns the value the dropdown should display for a given field on a
  // given member: a pending edit if one exists, otherwise the value the
  // backend gave us. Previously this had no `employment_type` case and
  // fell through to `m.role`, so the Type dropdown showed the role string,
  // failed to match either option, and silently rendered the default.
  const staged = (m, field) => {
    const draft = edits[m.id]?.[field];
    if (draft !== undefined) return draft;
    switch (field) {
      case "department": {
        // Prefer the explicit department_id from the API. If the backend
        // is on a stale build that doesn't return it yet, fall back to
        // looking up the dept by name from the loaded `departments` list
        // so the dropdown still displays the current value instead of
        // silently defaulting to "No Department."
        if (m.department_id != null) return String(m.department_id);
        if (m.department) {
          const match = departments.find(
            (d) => (d.name || "").toLowerCase() === String(m.department).toLowerCase()
          );
          if (match) return String(match.id);
        }
        return "";
      }
      case "employment_type":
        return m.employment_type || "1099";
      case "role":
      default:
        return m.role || "Member";
    }
  };

  const setStage = (id, field, value) =>
    setEdits((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));

  const saveMember = async (m) => {
    const draft = edits[m.id];
    if (!draft) return;

    try {
      // role + employment type update
      if ((draft.role && draft.role !== m.role) || (draft.employment_type && draft.employment_type !== m.employment_type)) {
        const payload = {};
        if (draft.role) payload.role = draft.role;
        if (draft.employment_type) payload.employment_type = draft.employment_type;
        await fetchWithAuth(`/api/accounts/organization/members/${m.id}/update-role/`, {
          method: "POST",
          json: payload,
        });
      }

      // department update. fetchWithAuth throws on non-2xx and returns the
      // parsed body on success — there is no `.ok` on the resolved value,
      // so the previous `if (!d.ok)` check always fired even when the save
      // succeeded. Now we let the throw surface the real backend message.
      if (typeof draft.department !== "undefined" && draft.department !== (m.department_id ?? "")) {
        await fetchWithAuth(
          `/api/accounts/organization/member/${m.id}/assign-department/`,
          {
            method: "POST",
            json: { department_id: draft.department || null },
          }
        );
      }

      toast.success("Member updated.");
      setEdits((e) => {
        const n = { ...e };
        delete n[m.id];
        return n;
      });
      await loadMembers();
    } catch (err) {
      // The backend already gives us a useful message for both save paths
      // (role/comp + department). Bubble it up instead of the generic toast.
      toast.error(err?.message || "Could not save member.");
    }
  };

  const removeMember = async (m) => {
    // Removing somebody ends their engagement, so it also lifts the hold. Said in
    // the confirmation, because the person doing it is looking at a Remove button
    // and not at the Hide from public column.
    const held = !!m.roster_locked;
    if (
      !window.confirm(
        `Remove ${m.name || m.email} from the organization?` +
          (held
            ? "\n\nThey are currently hidden from the public. Removing them also " +
              "lifts that, so other companies will be able to find and contact them."
            : "")
      )
    ) return;
    try {
      // new endpoint below; add to urls (see backend patch)
      const res = await fetchWithAuth(`/api/accounts/organization/members/${m.id}/remove/`, {
        method: "POST",
      });
      if (!res.ok) throw new Error();
      toast.success(held ? "Member removed and no longer hidden." : "Member removed.");
      await loadMembers();
    } catch {
      toast.error("Remove failed.");
    }
  };

  // Hold somebody off the public surfaces, or give up the hold.
  //
  // Asymmetric on purpose. Holding is one click: it is the protective direction
  // and it is reversible. Releasing asks first, because the hold is the thing
  // the agency is protecting and the backend calls giving it up "a separate,
  // deliberate decision".
  // Who else signs contracts for the company. Only the Owner sets it, and only
  // for an Admin, Department Leader or Manager; the server checks both.
  const isOwnerViewing = org?.role === "Owner";
  const toggleSigning = async (m) => {
    const giving = !m.can_sign_contracts;
    if (
      giving &&
      !window.confirm(
        `Let ${m.name || m.email} sign contracts for the company?

` + "Their signature will bind the company, the same as yours."
      )
    )
      return;
    setSignBusy(m.id);
    try {
      await fetchWithAuth(`/api/accounts/organization/members/${m.id}/signing/`, {
        method: "POST",
        json: { can_sign: giving },
      });
      toast.success(giving ? "They can sign contracts now." : "Signing rights removed.");
      await loadMembers();
    } catch (err) {
      toast.error(err?.message || "Could not change that.");
    } finally {
      setSignBusy(null);
    }
  };

  const toggleHold = async (m) => {
    const releasing = !!m.roster_locked;
    if (
      releasing &&
      !window.confirm(
        `Put ${m.name || m.email} back on the public board?

` +
          "This gives up your hold. Anyone will be able to find them in search " +
          "and contact them directly."
      )
    )
      return;

    setHoldBusy(m.id);
    try {
      // These endpoints take the CustomUser id, not the membership id, because
      // the hold lives on the user -- it is meant to outlive the engagement.
      await fetchWithAuth(
        `/api/accounts/organization/roster/${m.user_id}/${releasing ? "release" : "hold"}/`,
        { method: "POST" }
      );
      toast.success(releasing ? "Back on the public board." : "Hidden from the public board.");
      await loadMembers();
    } catch (err) {
      // The backend refuses an owner (400) and somebody another org holds (409)
      // with a sentence meant to be read, so show that rather than a generic.
      toast.error(err?.message || "Could not change that.");
    } finally {
      setHoldBusy(null);
    }
  };

  const header = useMemo(() => <h1 className="text-2xl sm:text-3xl font-display italic text-ink">Team</h1>, []);

  if (loading) {
    return (
      <CompanyLayout org={org} header={header}>
        <div className="p-6 text-secondary">Loading…</div>
      </CompanyLayout>
    );
  }

  return (
    <CompanyLayout org={org} header={header}>
      {/* Company setting: are people added here hidden from other companies? */}
      <div className="mb-6 bg-surface border border-line rounded-2xl p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <div className="text-base sm:text-lg font-semibold text-ink">Hide new team members from other companies</div>
          <p className="text-xs sm:text-sm text-tertiary mt-1">
            When this is on, everyone you add is hidden from other companies on Sift while they are on your team, and
            becomes visible again when you remove them. It applies to people you add from now on; change anyone
            already on your team with the Hide from public box in the table below.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 text-sm font-medium text-ink shrink-0" style={{ minHeight: 44 }}>
          <input
            type="checkbox"
            className="h-5 w-5"
            aria-label="Hide new team members from other companies"
            checked={hideDefault}
            disabled={savingHideDefault}
            onChange={(e) => saveHideDefault(e.target.checked)}
          />
          {hideDefault ? "On" : "Off"}
        </label>
      </div>

      {/* Single invite */}
      <div className="mb-6 bg-surface border border-line rounded-2xl p-3 sm:p-4">
        <div className="text-base sm:text-lg font-semibold text-ink mb-3">Add teammate</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <input
            type="text"
            placeholder="First name"
            aria-label="First name"
            autoComplete="off"
            value={invite.first_name}
            onChange={(e) => setInvite((f) => ({ ...f, first_name: e.target.value }))}
            className="bg-surface border border-line-control rounded-lg p-2 text-ink placeholder-faint text-sm sm:text-base focus:border-brand [color-scheme:light]"
          />
          <input
            type="text"
            placeholder="Last name"
            aria-label="Last name"
            autoComplete="off"
            value={invite.last_name}
            onChange={(e) => setInvite((f) => ({ ...f, last_name: e.target.value }))}
            className="bg-surface border border-line-control rounded-lg p-2 text-ink placeholder-faint text-sm sm:text-base focus:border-brand [color-scheme:light]"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1.5fr)_auto_auto_auto] gap-3 items-center">
          <input
            aria-label="Email"
            type="email"
            placeholder="Email"
            value={invite.email}
            onChange={(e) => setInvite((f) => ({ ...f, email: e.target.value }))}
            className="bg-surface border border-line-control rounded-lg p-2 text-ink placeholder-faint text-sm sm:text-base focus:border-brand [color-scheme:light]"
          />
          <select aria-label="Role"
            value={invite.role}
            onChange={(e) => setInvite((f) => ({ ...f, role: e.target.value }))}
            className="bg-surface border border-line-control rounded-lg p-2 text-ink text-sm sm:text-base focus:border-brand [color-scheme:light]"
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <select aria-label="Department"
            value={invite.department}
            onChange={(e) => setInvite((f) => ({ ...f, department: e.target.value }))}
            className="bg-surface border border-line-control rounded-lg p-2 text-ink text-sm sm:text-base focus:border-brand [color-scheme:light]"
          >
            <option value="">No department</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
          <select
            value={invite.employment_type}
            onChange={(e) => setInvite((f) => ({ ...f, employment_type: e.target.value }))}
            aria-label="Type"
            className="bg-surface border border-line-control rounded-lg p-2 text-ink text-sm sm:text-base focus:border-brand [color-scheme:light]"
          >
            <option value="1099">1099</option>
            <option value="W-2">W-2</option>
          </select>
          <label
            className="flex items-center gap-2 text-sm text-ink"
            title="Hidden from the public board, from search, and from profile pages. Nobody outside your organization can find or contact them."
          >
            <input
              type="checkbox"
              checked={!!invite.hide}
              disabled={invite.role === "Owner"}
              onChange={(e) => setInvite((f) => ({ ...f, hide: e.target.checked }))}
              className="h-4 w-4 accent-brand"
            />
            Hide from public
          </label>
          <button onClick={addMember} className="bg-brand text-white px-4 py-2 rounded-pill hover:bg-brand-hover text-sm sm:text-base">
            Invite
          </button>
        </div>
      </div>

      {/* CSV bulk upload */}
      <div className="mb-6 bg-surface border border-line rounded-2xl p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
          <div className="text-base sm:text-lg font-semibold text-ink">Bulk upload</div>
          <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={downloadCsvTemplate}
            className="border border-line-strong bg-surface text-ink px-3 py-2 rounded-pill hover:border-brand hover:text-brand text-sm"
          >
            Download template
          </button>
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <span className="border border-line-strong bg-surface text-ink px-3 py-2 rounded-pill hover:border-brand hover:text-brand">
              Upload CSV or Excel
            </span>
            <input
              type="file"
              accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                // Cleared so re-picking the same file after fixing it fires again.
                e.target.value = "";
                if (f) handleCsvFile(f);
              }}
            />
          </label>
          </div>
        </div>
        <p className="text-xs text-faint">
          A CSV or Excel (.xlsx) file. Columns: <code>first_name, last_name, email, role, department, type, hide_from_public</code>. Only
          email is required. Each person confirms their name when they open their invite, and adds anything else
          themselves. With a header row, columns can be in any order. Type is 1099 or W-2. For Hide from public,
          put x to hide someone; leave it blank and the setting above decides. Invites are emailed the moment
          you press Send Invites, and each link works for 3 days.
        </p>
        {csvError && (
          <div role="alert" className="mt-3 border border-red-200 bg-red-50 rounded-lg p-3 text-xs text-red-800">
            {csvError}
          </div>
        )}
        {csvFailures.length > 0 && (
          <div className="mt-3 border border-red-200 bg-red-50 rounded-lg p-3 text-xs text-red-800 space-y-1">
            <div className="font-semibold">These rows were not added:</div>
            {csvFailures.map((f) => (
              <div key={f.row}>Row {f.row}{f.email ? ` (${f.email})` : ""}: {f.error}</div>
            ))}
          </div>
        )}
        {csvRows.length > 0 && (
          <div className="mt-3 max-h-64 overflow-auto border border-line rounded-lg">
            <table className="w-full text-xs">
              <caption className="sr-only">People who will be invited</caption>
              <thead>
                <tr className="text-left bg-sunken text-tertiary">
                  <th className="px-2 py-1.5">Name</th>
                  <th className="px-2 py-1.5">Email</th>
                  <th className="px-2 py-1.5">Role</th>
                  <th className="px-2 py-1.5">Type</th>
                  <th className="px-2 py-1.5">Hidden</th>
                </tr>
              </thead>
              <tbody>
                {csvRows.map((r) => (
                  <tr key={r.email} className="border-t border-line">
                    <td className="px-2 py-1.5 text-ink">{[r.first_name, r.last_name].filter(Boolean).join(" ") || "Not given"}</td>
                    <td className="px-2 py-1.5 text-ink">{r.email}</td>
                    <td className="px-2 py-1.5">{r.role}</td>
                    <td className="px-2 py-1.5">{r.employment_type}</td>
                    <td className="px-2 py-1.5">{r.hide_from_public ? "Yes" : "No"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {csvRows.length > 0 && (
          <div className="mt-3 text-sm text-secondary flex flex-col sm:flex-row sm:items-center gap-2">
            <span>
              {csvRows.length} {csvRows.length === 1 ? "person" : "people"} ready to invite.
              {csvSkipped > 0 && ` ${csvSkipped} ${csvSkipped === 1 ? "row was" : "rows were"} skipped: no valid email.`}
            </span>
            <button
              onClick={sendCsvInvites}
              disabled={csvSending}
              className="bg-brand hover:bg-brand-hover text-white px-3 py-2 rounded-pill text-sm sm:inline-block w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {csvSending ? "Sending..." : "Send Invites"}
            </button>
          </div>
        )}
      </div>

      <ApprovalCover meId={meId} members={members} departments={departments} />

      {/* Members table */}
      <div className="bg-surface border border-line rounded-2xl overflow-hidden">
        <div className="p-3 border-b border-line font-semibold text-ink text-sm sm:text-base">Members</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm">
            <thead>
              <tr className="text-left bg-sunken text-xs text-tertiary">
                <th className="px-2 sm:px-3 py-2">Name</th>
                <th className="px-2 sm:px-3 py-2">Email</th>
                <th className="px-2 sm:px-3 py-2">Role</th>
                <th className="px-2 sm:px-3 py-2">Type</th>
                <th className="px-2 sm:px-3 py-2">Department</th>
                <th
                  className="px-2 sm:px-3 py-2"
                  title="Hidden from the public board, from search, and from profile pages. Nobody outside your organization can find or contact them."
                >
                  Hide from public
                </th>
                <th
                  className="px-2 sm:px-3 py-2"
                  title="Signs contracts for the company. The Owner always can, and decides who else does."
                >
                  Signs contracts
                </th>
                <th className="px-2 sm:px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => {
                const roleNow = staged(m, "role") || m.role;
                const isClockOnly = roleNow === "Time Entry - Clock";
                const isManualOnly = roleNow === "Time Entry - Manual";
                const isTimeEntry = isClockOnly || isManualOnly;
                return (
                <tr key={m.id} className="border-t border-line hover:bg-sunken">
                  <td className="px-2 sm:px-3 py-2 max-w-[160px] sm:max-w-none text-ink">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="min-w-0 break-words">{m.name || "-"}</span>
                      {isTimeEntry && (
                        <span
                          className="text-[0.625rem] px-1.5 py-0.5 rounded-pill text-gold-ink bg-[#FBF3E2] whitespace-nowrap"
                          title={
                            isClockOnly
                              ? "Time Entry only: restricted to the /clock page (clock in / out)"
                              : "Time Entry only: restricted to the /clock page (manual time entry)"
                          }
                        >
                          {isClockOnly ? "Clock-in only" : "Manual entry only"}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 sm:px-3 py-2 leading-tight [overflow-wrap:anywhere] max-w-[150px] sm:max-w-none text-secondary">{m.email}</td>
                  <td className="px-2 sm:px-3 py-2">
                    <select aria-label={`Role for ${m.name || m.email}`}
                      value={staged(m, "role")}
                      onChange={(e) => setStage(m.id, "role", e.target.value)}
                      className="bg-surface border border-line-control rounded-lg p-2 text-ink text-xs sm:text-sm w-full focus:border-brand [color-scheme:light]"
                    >
                      {ROLES.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 sm:px-3 py-2">
                    <select aria-label={`Worker type for ${m.name || m.email}`}
                      value={staged(m, "employment_type") || "1099"}
                      onChange={(e) => setStage(m.id, "employment_type", e.target.value)}
                      className="bg-surface border border-line-control rounded-lg p-2 text-ink text-xs sm:text-sm w-full focus:border-brand [color-scheme:light]"
                    >
                      <option value="1099">1099</option>
                      <option value="W-2">W-2</option>
                    </select>
                  </td>
                  <td className="px-2 sm:px-3 py-2">
                    <select aria-label={`Department for ${m.name || m.email}`}
                      value={String(staged(m, "department") || "")}
                      onChange={(e) => setStage(m.id, "department", e.target.value)}
                      className="bg-surface border border-line-control rounded-lg p-2 text-ink text-xs sm:text-sm w-full focus:border-brand [color-scheme:light]"
                    >
                      <option value="">No department</option>
                      {departments.map((d) => (
                        <option key={d.id} value={String(d.id)}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 sm:px-3 py-2">
                    {m.roster_locked && !m.roster_is_ours ? (
                      // Another organization holds this person. A live checkbox
                      // here would be one that never sticks -- the backend
                      // answers 409 -- so say why instead of offering it.
                      <span
                        className="text-[0.6875rem] text-tertiary whitespace-nowrap"
                        title="Another organization holds this person. They have to release them first."
                      >
                        Held elsewhere
                      </span>
                    ) : (
                      <label
                        className="inline-flex items-center gap-2"
                        title={
                          m.role === "Owner"
                            ? "An owner cannot be hidden this way. Change their role first."
                            : "Hide this person from the public board, search and profile pages."
                        }
                      >
                        <input
                          type="checkbox"
                          checked={!!m.roster_locked}
                          disabled={m.role === "Owner" || holdBusy === m.id}
                          onChange={() => toggleHold(m)}
                          className="h-4 w-4 accent-brand disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                        />
                        {m.roster_locked && m.roster_held_until && (
                          <span
                            className="text-[0.625rem] text-tertiary whitespace-nowrap"
                            title="When this hold lapses on its own."
                          >
                            to {new Date(m.roster_held_until).toLocaleDateString()}
                          </span>
                        )}
                      </label>
                    )}
                  </td>
                  <td className="px-2 sm:px-3 py-2">
                    {m.role === "Owner" ? (
                      <span className="text-[0.6875rem] text-tertiary whitespace-nowrap">Always</span>
                    ) : ["Admin", "Department Leader", "Manager"].includes(m.role) ? (
                      <label
                        className="inline-flex items-center gap-2"
                        title={
                          isOwnerViewing
                            ? "Let this person sign contracts for the company."
                            : "Only the Owner decides who signs for the company."
                        }
                      >
                        <input
                          type="checkbox"
                          aria-label={`${m.name || m.email} signs contracts for the company`}
                          checked={!!m.can_sign_contracts}
                          disabled={!isOwnerViewing || signBusy === m.id}
                          onChange={() => toggleSigning(m)}
                          className="h-4 w-4 accent-brand disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                        />
                      </label>
                    ) : (
                      <span
                        className="text-[0.6875rem] text-tertiary"
                        title="Only an Admin, Department Leader or Manager can be given signing rights."
                      >
                        No
                      </span>
                    )}
                  </td>
                  <td className="px-2 sm:px-3 py-2">
                    <div className="flex flex-col sm:flex-row gap-1 sm:gap-2">
                      <button
                        onClick={() => saveMember(m)}
                        className="text-xs bg-brand text-white px-3 py-2 rounded-pill hover:bg-brand-hover whitespace-nowrap"
                      >
                        Save
                      </button>
                      <button
                        onClick={() =>
                          setEdits((e) => {
                            const n = { ...e };
                            delete n[m.id];
                            return n;
                          })
                        }
                        className="text-xs border border-line-strong bg-surface text-ink px-3 py-2 rounded-pill hover:border-brand hover:text-brand whitespace-nowrap"
                      >
                        Reset
                      </button>
                      <button
                        onClick={() => removeMember(m)}
                        className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-pill whitespace-nowrap"
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
              {members.length === 0 && (
                <tr>
                  <td className="px-3 py-3 text-tertiary" colSpan={7}>
                    No members.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </CompanyLayout>
  );
}